import { translateApiMessage } from '../i18n'

/** An error the server described in its response body. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    /** The server's own message, untouched — useful in logs and bug reports. */
    readonly serverMessage: string,
    /**
     * The decoded body, kept whole.
     *
     * Some refusals carry more than a sentence. A 409 on a draft decision comes
     * back with the draft as it now stands, and that is the entire point of the
     * response: without it the screen would have to ask again, and could lose
     * the same race a second time.
     */
    readonly body: unknown = undefined,
  ) {
    super(translateApiMessage(serverMessage))
    this.name = 'ApiError'
  }
}

/** The request never reached the server, or the answer was unreadable. */
export class NetworkError extends Error {
  constructor(reason: unknown) {
    // Through the same table as a server refusal, because it reaches a person
    // the same way: screens render error.message, and "network request failed"
    // was one of the sentences an owner who reads Russian was shown at the
    // moment something had already gone wrong.
    //
    // Kept as the standard `cause` so a devtools stack shows what actually broke.
    super(translateApiMessage('network request failed'), { cause: reason })
    this.name = 'NetworkError'
  }
}

/** The response arrived but did not have the shape we were promised. */
export class ContractError extends Error {
  constructor(detail: string) {
    super(`unexpected response shape: ${detail}`)
    this.name = 'ContractError'
  }
}

type UnauthorizedHandler = () => void

let onUnauthorized: UnauthorizedHandler | null = null

/**
 * Register what happens when any call comes back 401.
 *
 * Wired once at start-up to drop the cached user, so that a session expiring
 * mid-session lands on the sign-in screen instead of leaving a shell that shows
 * a name and refuses every action.
 */
export function setUnauthorizedHandler(handler: UnauthorizedHandler): void {
  onUnauthorized = handler
}

export interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  /**
   * The request body.
   *
   * Anything but `FormData` is sent as JSON. `FormData` is passed through
   * untouched and without a Content-Type of ours: the browser has to write that
   * header itself, because it carries the multipart boundary and a value we set
   * would be one the parser on the other end cannot use.
   */
  readonly body?: unknown
  readonly signal?: AbortSignal
  /**
   * Suppress the global "you are signed out" handler for this call.
   *
   * The sign-in endpoint answers 401 for a wrong password. That is a form
   * error, not an expired session, and running the sign-out path on it would be
   * both wrong and confusing.
   */
  readonly tolerate401?: boolean
}

interface RawResponse {
  readonly status: number
  readonly body: unknown
}

/** The cookie the server sets alongside the session, readable on purpose. */
const CSRF_COOKIE = 'rr_csrf'
const CSRF_HEADER = 'X-CSRF-Token'

/**
 * Echo the CSRF token back on anything that changes something.
 *
 * The session cookie is HttpOnly and travels on its own; this one is not, and
 * has to be read and sent by hand. That asymmetry is the whole mechanism: a page
 * on another origin can make the browser send our cookies but cannot read them,
 * so it cannot set this header.
 *
 * Reads send nothing, because the server does not ask for it there.
 */
function csrfHeader(method: string): Record<string, string> {
  if (method === 'GET' || method === 'HEAD') return {}

  const token = readCookie(CSRF_COOKIE)
  // Missing means the session predates the token or has expired. Sending the
  // request anyway is right: the server answers with the csrf code, and the
  // panel tells the person to sign in again rather than guessing here.
  return token === undefined ? {} : { [CSRF_HEADER]: token }
}

function readCookie(name: string): string | undefined {
  // The contract checks run this module under Node, where there is no document.
  // A missing token is a case the server already answers clearly, so there is
  // nothing to invent here.
  if (typeof document === 'undefined') return undefined

  for (const part of document.cookie.split(';')) {
    const [key, ...rest] = part.trim().split('=')
    if (key === name) return decodeURIComponent(rest.join('='))
  }
  return undefined
}

function isFormData(body: unknown): body is FormData {
  return typeof FormData !== 'undefined' && body instanceof FormData
}

async function send(path: string, options: RequestOptions): Promise<RawResponse> {
  const { method = 'GET', body, signal, tolerate401 = false } = options

  let response: Response
  try {
    response = await fetch(path, {
      method,
      // The session is an HttpOnly cookie: unreadable from JavaScript by design,
      // so we never touch it — we only ask the browser to send it.
      credentials: 'include',
      headers: {
        ...(body === undefined || isFormData(body) ? {} : { 'Content-Type': 'application/json' }),
        ...csrfHeader(method),
      },
      ...(body === undefined ? {} : { body: isFormData(body) ? body : JSON.stringify(body) }),
      ...(signal === undefined ? {} : { signal }),
    })
  } catch (cause) {
    throw new NetworkError(cause)
  }

  if (response.status === 401 && !tolerate401) {
    onUnauthorized?.()
  }

  if (response.status === 204) {
    return { status: response.status, body: undefined }
  }

  const text = await response.text()
  if (text === '') {
    return { status: response.status, body: undefined }
  }

  try {
    return { status: response.status, body: JSON.parse(text) as unknown }
  } catch {
    // A non-JSON body from an endpoint that should speak JSON — a proxy error
    // page, or a route that does not exist. Keep a little of it for the log.
    if (!response.ok) {
      throw new ApiError(response.status, text.slice(0, 200))
    }
    throw new ContractError(`body is not JSON (status ${response.status})`)
  }
}

function errorMessage(body: unknown, status: number): string {
  if (typeof body === 'object' && body !== null) {
    const message = (body as { error?: unknown }).error
    if (typeof message === 'string' && message !== '') return message
  }
  return `request failed with status ${status}`
}

/**
 * Perform a request and hand the decoded body to `parse`.
 *
 * `parse` takes `unknown` on purpose: everything arriving over the network is
 * unknown until something checks it, and that check is the only place where the
 * shape becomes a type.
 */
export async function request<T>(
  path: string,
  parse: (body: unknown) => T,
  options: RequestOptions = {},
): Promise<T> {
  const { status, body } = await send(path, options)

  if (status < 200 || status >= 300) {
    throw new ApiError(status, errorMessage(body, status), body)
  }

  return parse(body)
}

/** For endpoints that answer 204 with no body. */
export async function requestEmpty(path: string, options: RequestOptions = {}): Promise<void> {
  const { status, body } = await send(path, options)

  if (status < 200 || status >= 300) {
    throw new ApiError(status, errorMessage(body, status), body)
  }
}
